/**
 * Status constants for all entities
 */
export const TenantStatuses = {
    ACTIVE: 'active',
    INACTIVE: 'inactive',
    SUSPENDED: 'suspended',
};
export const TenantStatusLabels = {
    [TenantStatuses.ACTIVE]: 'Active',
    [TenantStatuses.INACTIVE]: 'Inactive',
    [TenantStatuses.SUSPENDED]: 'Suspended',
};
export const UserRoles = {
    ADMIN: 'admin',
    MEMBER: 'member',
};
export const UserStatuses = {
    ACTIVE: 'active',
    INACTIVE: 'inactive',
    PENDING: 'pending',
};
export const ProjectStatuses = {
    ACTIVE: 'active',
    INACTIVE: 'inactive',
    ARCHIVED: 'archived',
};
export const ProjectStatusLabels = {
    [ProjectStatuses.ACTIVE]: 'Active',
    [ProjectStatuses.INACTIVE]: 'Inactive',
    [ProjectStatuses.ARCHIVED]: 'Archived',
};
export const TaskStatuses = {
    PENDING: 'pending',
    IN_PROGRESS: 'in_progress',
    COMPLETED: 'completed',
    FAILED: 'failed',
    CANCELLED: 'cancelled',
};
export const TaskStatusLabels = {
    [TaskStatuses.PENDING]: 'Pending',
    [TaskStatuses.IN_PROGRESS]: 'In Progress',
    [TaskStatuses.COMPLETED]: 'Completed',
    [TaskStatuses.FAILED]: 'Failed',
    [TaskStatuses.CANCELLED]: 'Cancelled',
};
export const PullRequestStatuses = {
    OPEN: 'open',
    CLOSED: 'closed',
    MERGED: 'merged',
};
export const PullRequestStatusLabels = {
    [PullRequestStatuses.OPEN]: 'Open',
    [PullRequestStatuses.CLOSED]: 'Closed',
    [PullRequestStatuses.MERGED]: 'Merged',
};
export const PRReviewStatuses = {
    APPROVED: 'approved',
    REJECTED: 'rejected',
    PARTIALLY_APPROVED: 'partially_approved',
    OPEN: 'open',
};
export const PRReviewStatusLabels = {
    [PRReviewStatuses.APPROVED]: 'Approved',
    [PRReviewStatuses.REJECTED]: 'Rejected',
    [PRReviewStatuses.PARTIALLY_APPROVED]: 'Partially Approved',
    [PRReviewStatuses.OPEN]: 'Open',
};
/**
 * Evidence-backed reasons the agent may finish a task without opening a PR.
 * A failure to reproduce on its own is not one of them - that is a blocker.
 * needs_product_decision stops a fix that would miss the ticket's expected
 * outcome until a human decides the open question. needs_manual_reproduction
 * stops when the only unproven link needs an environment the agent can't run,
 * such as Safari or a native password manager, until a human reports what a
 * manual check showed.
 */
export const NoPrCategories = {
    ALREADY_FIXED: 'already_fixed',
    WORKING_AS_DESIGNED: 'working_as_designed',
    NOT_A_CODE_DEFECT: 'not_a_code_defect',
    NEEDS_PRODUCT_DECISION: 'needs_product_decision',
    NEEDS_MANUAL_REPRODUCTION: 'needs_manual_reproduction',
};
export const NoPrCategoryLabels = {
    [NoPrCategories.ALREADY_FIXED]: 'Already fixed',
    [NoPrCategories.WORKING_AS_DESIGNED]: 'Working as designed',
    [NoPrCategories.NOT_A_CODE_DEFECT]: 'Not a code defect',
    [NoPrCategories.NEEDS_PRODUCT_DECISION]: 'Needs product decision',
    [NoPrCategories.NEEDS_MANUAL_REPRODUCTION]: 'Needs manual reproduction',
};
/**
 * Why a follow-up run exists. A decision run answers the product question a
 * no-PR task asked; a revision run takes feedback on the ticket's open PRs
 * and pushes to the same branches.
 */
export const FollowUpModes = {
    DECISION: 'decision',
    REVISION: 'revision',
};
/**
 * Where the @mention that started a follow-up run was posted. The trigger
 * comment ID is stored with a matching prefix for GitHub sources, so IDs from
 * different systems can't collide in the one-run-per-comment index.
 */
export const FollowUpTriggerSources = {
    JIRA_COMMENT: 'jira_comment',
    GITHUB_REVIEW: 'github_review',
    GITHUB_REVIEW_COMMENT: 'github_review_comment',
    GITHUB_COMMENT: 'github_comment',
};
/**
 * Revision runs allowed per ticket before ResolveAI hands the PRs to a
 * developer. Counted since the ticket's latest non-follow-up task, so an
 * admin re-import starts a fresh budget. Also stops two bots from keeping
 * each other busy.
 */
export const MAX_REVISION_ROUNDS = 3;
/**
 * Jira event set the tenant webhook must be registered with. Bump it
 * whenever the registered events change; the webhook-refresh job re-registers
 * any webhook stored with a lower version, since Jira can't edit events in place.
 * v2 added comment_created, for follow-up runs after a product decision.
 */
export const JIRA_WEBHOOK_EVENTS_VERSION = 2;
export const JIRA_WEBHOOK_EVENTS = [
    'jira:issue_created',
    'jira:issue_updated',
    'jira:issue_deleted',
    'comment_created',
];
/**
 * Human review of a no-PR decision - the stand-in for a PR being merged or
 * closed, so the agent's accuracy can be tracked on runs without a PR.
 */
export const NoPrReviewStatuses = {
    PENDING: 'pending',
    APPROVED: 'approved',
    REJECTED: 'rejected',
};
export const NoPrReviewStatusLabels = {
    [NoPrReviewStatuses.PENDING]: 'Awaiting review',
    [NoPrReviewStatuses.APPROVED]: 'Approved',
    [NoPrReviewStatuses.REJECTED]: 'Rejected',
};
export const PlatformAdminStatuses = {
    ACTIVE: 'active',
    INACTIVE: 'inactive',
};
export const CredentialSources = {
    GOOGLE: 'google',
    EMAIL: 'email',
};
export const PromptCategories = {
    TICKET_PARSING: 'ticket_parsing',
    CODE_SEARCH: 'code_search',
    ROOT_CAUSE_ANALYSIS: 'root_cause_analysis',
    FIX_PLANNING: 'fix_planning',
    CODE_GENERATION: 'code_generation',
    CODE_REVIEW: 'code_review',
    PR_DESCRIPTION: 'pr_description',
    TICKET_COMMENT: 'ticket_comment',
};
/**
 * Agent orchestration phases - single source of truth shared between the
 * agent's sub-agent orchestration (apps/agent/src/types/sub-agents.ts,
 * re-exported as SubAgents) and API-side validation of which phases a
 * project knowledge entry applies to.
 */
export const AgentPhases = {
    PARSE_TICKET: 'parse_ticket',
    SEARCH_CODEBASE: 'search_codebase',
    ANALYZE_ROOT_CAUSE: 'analyze_root_cause',
    PLAN_FIX: 'plan_fix',
    GENERATE_FIX: 'generate_fix',
    SELF_REVIEW: 'self_review',
    CREATE_PR: 'create_pr',
    UPDATE_TICKET: 'update_ticket',
};
export const AgentPhaseLabels = {
    [AgentPhases.PARSE_TICKET]: 'Parse Ticket',
    [AgentPhases.SEARCH_CODEBASE]: 'Search Codebase',
    [AgentPhases.ANALYZE_ROOT_CAUSE]: 'Root Cause Analysis',
    [AgentPhases.PLAN_FIX]: 'Fix Planning',
    [AgentPhases.GENERATE_FIX]: 'Fix Generation',
    [AgentPhases.SELF_REVIEW]: 'Self Review',
    [AgentPhases.CREATE_PR]: 'Create PR',
    [AgentPhases.UPDATE_TICKET]: 'Update Ticket',
};
export const KnowledgeCategories = {
    ARCHITECTURE: 'architecture',
    CONVENTION: 'convention',
    GLOSSARY: 'glossary',
    GOTCHA: 'gotcha',
    RUNBOOK: 'runbook',
};
export const KnowledgeCategoryLabels = {
    [KnowledgeCategories.ARCHITECTURE]: 'Architecture',
    [KnowledgeCategories.CONVENTION]: 'Convention',
    [KnowledgeCategories.GLOSSARY]: 'Glossary',
    [KnowledgeCategories.GOTCHA]: 'Gotcha',
    [KnowledgeCategories.RUNBOOK]: 'Runbook',
};
export const KnowledgeSources = {
    MANUAL: 'manual',
    REPO_IMPORT: 'repo_import',
};
/**
 * Knowledge entry budgets - kept small deliberately: pinned entries are
 * injected into the master's user message on every task, and every enabled
 * entry's name/description sits in every sub-agent's context from session
 * start regardless of whether it is ever retrieved.
 */
export const KNOWLEDGE_BODY_MAX_BYTES_PINNED = 8 * 1024;
export const KNOWLEDGE_BODY_MAX_BYTES_UNPINNED = 32 * 1024;
export const KNOWLEDGE_PINNED_TOKEN_BUDGET = 8000;
// Rough chars-per-token estimate for English/code prose, used only to warn
// early at save time - the agent-side render is the actual source of truth.
export const KNOWLEDGE_CHARS_PER_TOKEN_ESTIMATE = 4;
export const KNOWLEDGE_MAX_ENABLED_ENTRIES = 50;
/**
 * Knowledge base file attachments. The agent buffers every retrievable
 * attachment in memory at task start, so the per-file cap bounds that cost.
 * Enforced in the browser, at upload-URL minting, by S3 itself (the declared
 * size is signed into the presigned PUT), and again after upload.
 */
export const KNOWLEDGE_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;
const MARKDOWN_TYPES = ['text/markdown', 'text/x-markdown', 'text/plain'];
const YAML_TYPES = ['application/yaml', 'application/x-yaml', 'text/yaml', 'text/x-yaml', 'text/plain'];
/**
 * Allowed attachment types, keyed by lower-case file extension. The
 * spreadsheet and office formats match the reading skills the agent ships
 * (apps/agent/src/services/knowledge-file-skills.ts). Browsers often send
 * application/octet-stream (or nothing) for less common types, so that is
 * accepted for any allowlisted extension.
 */
export const KNOWLEDGE_ATTACHMENT_TYPES = {
    md: MARKDOWN_TYPES,
    markdown: MARKDOWN_TYPES,
    txt: ['text/plain'],
    log: ['text/plain', 'text/x-log'],
    json: ['application/json', 'text/json'],
    yaml: YAML_TYPES,
    yml: YAML_TYPES,
    csv: ['text/csv', 'application/csv', 'application/vnd.ms-excel', 'text/plain'],
    tsv: ['text/tab-separated-values', 'text/plain'],
    pdf: ['application/pdf'],
    png: ['image/png'],
    jpg: ['image/jpeg'],
    jpeg: ['image/jpeg'],
    gif: ['image/gif'],
    webp: ['image/webp'],
    xlsx: ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
    xlsm: ['application/vnd.ms-excel.sheet.macroenabled.12'],
    xls: ['application/vnd.ms-excel'],
    ods: ['application/vnd.oasis.opendocument.spreadsheet'],
    docx: ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
    doc: ['application/msword'],
    pptx: ['application/vnd.openxmlformats-officedocument.presentationml.presentation'],
    odt: ['application/vnd.oasis.opendocument.text'],
};
const GENERIC_BINARY_CONTENT_TYPE = 'application/octet-stream';
function getFileExtension(filename) {
    const lastDot = filename.lastIndexOf('.');
    if (lastDot <= 0 || lastDot === filename.length - 1)
        return null;
    return filename.slice(lastDot + 1).toLowerCase();
}
/**
 * Validates a knowledge attachment against the size cap and type allowlist.
 * Returns a user-facing error message, or null when the file is acceptable.
 * Shared by the browser pre-check and the API schemas so both give the same
 * answer.
 */
export function getKnowledgeAttachmentError(file) {
    const maxMb = KNOWLEDGE_ATTACHMENT_MAX_BYTES / (1024 * 1024);
    if (file.sizeBytes <= 0) {
        return 'The selected file is empty';
    }
    if (file.sizeBytes > KNOWLEDGE_ATTACHMENT_MAX_BYTES) {
        return `Files must be ${maxMb} MB or smaller`;
    }
    const extension = getFileExtension(file.filename);
    const allowedTypes = extension ? KNOWLEDGE_ATTACHMENT_TYPES[extension] : undefined;
    const supportedList = Object.keys(KNOWLEDGE_ATTACHMENT_TYPES).join(', ');
    if (!allowedTypes) {
        return `This file type is not supported. Supported types: ${supportedList}`;
    }
    const contentType = file.contentType.split(';')[0].trim().toLowerCase();
    const isGenericBinary = contentType === GENERIC_BINARY_CONTENT_TYPE;
    if (!isGenericBinary && !allowedTypes.includes(contentType)) {
        return `The file's content type (${contentType}) is not supported for .${extension} files`;
    }
    return null;
}
/**
 * AI Model configuration - single source of truth for model name, version, and pricing
 * Cost values are in cents per 1M tokens. cacheReadMultiplier is the price of a
 * cache hit relative to a fresh input token (0.05x on Claude Opus 5.5).
 */
export const AiModelConfig = {
    haiku: {
        name: 'haiku',
        version: 'claude-haiku-4-5-20251001',
        inputCostPerMillionTokens: 100, // $1 per 1M tokens
        outputCostPerMillionTokens: 500, // $5 per 1M tokens
        cacheReadMultiplier: 0.1,
    },
    sonnet: {
        name: 'sonnet',
        version: 'claude-sonnet-5-5',
        inputCostPerMillionTokens: 200, // $2 per 1M tokens
        outputCostPerMillionTokens: 1000, // $10 per 1M tokens
        cacheReadMultiplier: 0.1,
    },
    opus: {
        name: 'opus',
        version: 'claude-opus-5-5',
        inputCostPerMillionTokens: 400, // $4 per 1M tokens
        outputCostPerMillionTokens: 2000, // $20 per 1M tokens
        cacheReadMultiplier: 0.05, // $0.20 per 1M cache hits
    },
};
/**
 * Model tiers a platform admin can pick from, cheapest first
 */
export const AI_MODEL_TIERS = ['haiku', 'sonnet', 'opus'];
/**
 * Agents whose model can be overridden per tenant from the control plane.
 * Sub-agent keys match the agent's SubAgents; master_orchestrator matches the
 * usage-tracker category recorded for the orchestrator's own turns.
 */
export const AgentModelKeys = {
    MASTER_ORCHESTRATOR: 'master_orchestrator',
    PARSE_TICKET: 'parse_ticket',
    SEARCH_CODEBASE: 'search_codebase',
    ANALYZE_ROOT_CAUSE: 'analyze_root_cause',
    PLAN_FIX: 'plan_fix',
    GENERATE_FIX: 'generate_fix',
    SELF_REVIEW: 'self_review',
    CREATE_PR: 'create_pr',
    UPDATE_TICKET: 'update_ticket',
};
/**
 * Default model tier per agent, used when a tenant has no override
 */
export const DEFAULT_AGENT_MODELS = {
    [AgentModelKeys.MASTER_ORCHESTRATOR]: 'sonnet',
    [AgentModelKeys.PARSE_TICKET]: 'sonnet',
    [AgentModelKeys.SEARCH_CODEBASE]: 'sonnet',
    [AgentModelKeys.ANALYZE_ROOT_CAUSE]: 'opus',
    [AgentModelKeys.PLAN_FIX]: 'opus',
    [AgentModelKeys.GENERATE_FIX]: 'sonnet',
    [AgentModelKeys.SELF_REVIEW]: 'sonnet',
    [AgentModelKeys.CREATE_PR]: 'haiku',
    [AgentModelKeys.UPDATE_TICKET]: 'haiku',
};
export const AgentModelLabels = {
    [AgentModelKeys.MASTER_ORCHESTRATOR]: 'Master Orchestrator',
    [AgentModelKeys.PARSE_TICKET]: 'Parse Ticket',
    [AgentModelKeys.SEARCH_CODEBASE]: 'Search Codebase',
    [AgentModelKeys.ANALYZE_ROOT_CAUSE]: 'Analyze Root Cause',
    [AgentModelKeys.PLAN_FIX]: 'Plan Fix',
    [AgentModelKeys.GENERATE_FIX]: 'Generate Fix',
    [AgentModelKeys.SELF_REVIEW]: 'Self Review',
    [AgentModelKeys.CREATE_PR]: 'Create PR',
    [AgentModelKeys.UPDATE_TICKET]: 'Update Ticket',
};
/**
 * Delay before an assignment-triggered task is picked up (5 minutes)
 */
export const TASK_PICKUP_DELAY_SECONDS = 300;
/**
 * Delay before a manually imported task is picked up (30 seconds) - short
 * enough to feel immediate, long enough to give the admin a window to
 * cancel it before the ECS task actually launches.
 */
export const MANUAL_IMPORT_DELAY_SECONDS = 30;
/**
 * code-review-graph venv binary path inside the agent/mcp-server container
 * (see apps/agent/Dockerfile). Shared because both the agent (spawning the
 * MCP server) and the MCP server (running deterministic build/update after
 * clone and commit) need the same path.
 */
export const CODE_REVIEW_GRAPH_BINARY_PATH = '/opt/crg-venv/bin/code-review-graph';
/**
 * Subdirectory (under a tenant's repos path) where Playwright MCP writes
 * screenshots. Shared because the agent (which passes --output-dir when
 * spawning Playwright MCP) and the MCP server (whose PathValidator resolves
 * the upload tool's filename against this same subdir) must agree on one
 * path without an absolute path crossing the process boundary - see
 * buildScreenshotOutputDir. Dot-prefixed so it stays out of code_search /
 * list_files results and isn't picked up by github_commit's stage-all.
 */
export const SCREENSHOT_OUTPUT_SUBDIR = '.screenshots';
/**
 * Subdirectory (under a tenant's repos path) the agent materializes skills
 * into and runs the SDK from. Playwright MCP takes its workspace from the
 * SDK's roots, so an explicit browser_take_screenshot filename lands here,
 * not in SCREENSHOT_OUTPUT_SUBDIR (--output-dir only applies to screenshots
 * Playwright names itself). The upload tool checks both.
 */
export const AGENT_WORKSPACE_SUBDIR = '_skills';
/**
 * Build the directory Playwright MCP should write screenshots to for a
 * given tenant. Both the agent (building this path to pass as --output-dir)
 * and the MCP server (whose PathValidator is rooted at
 * `{reposBasePath}/{tenantId}`) must derive it from the same two values.
 */
export function buildScreenshotOutputDir(reposBasePath, tenantId) {
    return `${reposBasePath}/${tenantId}/${SCREENSHOT_OUTPUT_SUBDIR}`;
}
/**
 * PR sync interval in minutes
 */
export const PR_SYNC_INTERVAL_MINUTES = 30;
/**
 * Magic link expiry in minutes
 */
export const MAGIC_LINK_EXPIRY_MINUTES = 15;
/**
 * Access token expiry in seconds (1 hour)
 */
export const ACCESS_TOKEN_EXPIRY_SECONDS = 3600;
/**
 * Refresh token expiry in seconds (7 days)
 */
export const REFRESH_TOKEN_EXPIRY_SECONDS = 604800;
/**
 * OAuth transfer code expiry in seconds (60 seconds)
 */
export const OAUTH_TRANSFER_EXPIRY_SECONDS = 60;
/**
 * Stall timeout for a master agent orchestration run (10 minutes).
 * This is an IDLE timeout, not a total-duration timeout: it resets every time
 * the Claude Agent SDK yields a message, so long-running-but-progressing runs
 * (known to take well over an hour) are unaffected. It only aborts when the
 * SDK goes completely silent for this long with no tool call outstanding
 * (e.g. a stuck MCP server subprocess never completing its handshake), so a
 * hung task fails fast instead of running forever. A running tool call is
 * bounded by ORCHESTRATION_MAX_TOOL_DURATION_MS instead - see
 * OrchestrationMonitor.
 */
export const ORCHESTRATION_STALL_TIMEOUT_MS = 10 * 60 * 1000;
/**
 * Longest a single `code_execute_command` may run (15 minutes). Must stay
 * below MCP_TOOL_TIMEOUT_MS so the command reports its own timeout.
 */
export const CODE_COMMAND_MAX_TIMEOUT_MS = 15 * 60 * 1000;
/**
 * Hard wall-clock ceiling for a single MCP tool call (20 minutes).
 *
 * The Claude Agent SDK's bundled CLI applies an effectively unbounded default
 * per-call MCP timeout, and its `tool_progress` heartbeats keep the
 * orchestration idle timer alive for as long as a call is outstanding. Without
 * an explicit ceiling a hung MCP tool therefore parks the whole run past any
 * idle guard. Progress notifications do NOT extend this limit.
 *
 * Must stay above CODE_COMMAND_MAX_TIMEOUT_MS so a legitimately long
 * `code_execute_command` returns its own timeout error rather than being cut
 * off mid-flight.
 */
export const MCP_TOOL_TIMEOUT_MS = 20 * 60 * 1000;
/**
 * Hard wall-clock ceiling for a single Playwright MCP tool call (5 minutes).
 * Browser actions (navigate, click, screenshot) are interactive-scale, so they
 * get a much tighter ceiling than the repository tools in resolve-ai-mcp.
 */
export const MCP_BROWSER_TOOL_TIMEOUT_MS = 5 * 60 * 1000;
/**
 * In-process backstop on a single tool call (25 minutes).
 *
 * Deliberately above every MCP_*_TOOL_TIMEOUT_MS so the CLI's own per-call
 * timeout fires first and surfaces a clean tool error. This only engages when
 * that timeout does not fire at all - a non-MCP builtin tool that hangs, or a
 * CLI that fails to honour its own ceiling - in which case the run is aborted
 * rather than left parked behind an endless heartbeat stream.
 *
 * The `Agent` tool is exempt: sub-agent calls legitimately run for hours.
 */
export const ORCHESTRATION_MAX_TOOL_DURATION_MS = 25 * 60 * 1000;
//# sourceMappingURL=constants.js.map